import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  DocumentMagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const endpoint = '/api/audit/comprobantes/approval-history/';
const minDate = '2026-02-01';
const pageSizeOptions = [1, 25, 50, 100];

const todayISO = () => {
  const now = new Date();
  const value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return value < minDate ? minDate : value;
};

const money = (value) => {
  const numeric = Number(value);
  return value === null || value === undefined || !Number.isFinite(numeric)
    ? 'NO DISPONIBLE'
    : `$${numeric.toFixed(2)}`;
};
const evidenceMeta = {
  verified_system: { label: 'Sistema verificado', code: 'SYSTEM' },
  verified_legacy: { label: 'Legacy verificado', code: 'LEGACY' },
  state_only: { label: 'Sin bitácora', code: 'SIN BITACORA' },
};
const evidenceLabel = (value) => evidenceMeta[value]?.label || value || 'Sin evidencia';
const evidenceCode = (value) => evidenceMeta[value]?.code || String(value || 'SIN EVIDENCIA').toUpperCase();
const proofSourceLabel = (value) => (
  value === 'immutable_snapshot'
    ? 'ARCHIVO INMUTABLE'
    : value === 'current_link'
      ? 'ARCHIVO VINCULADO ACTUAL'
      : 'NO DISPONIBLE'
);

const proofType = (row) => {
  if (!['pdf', 'image'].includes(row?.proof_kind) || typeof row?.proof_url !== 'string') return null;
  try {
    const url = new URL(row.proof_url, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith('/') ? row.proof_kind : null;
  } catch {
    return null;
  }
};

const joinUpper = (values) => (
  Array.isArray(values) && values.length
    ? values.map((value) => String(value || '').toUpperCase()).join(', ')
    : 'NO DISPONIBLE'
);

const formatDateTime = (value) => {
  if (!value) return 'NO DISPONIBLE';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });
};

const CountCard = ({ label, value, code }) => (
  <article aria-label={label} className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-lg ring-1 ring-white/5">
    <p className="text-sm text-gray-300">{label}{code ? <span className="ml-2 text-xs font-bold text-notaria-yellow">{code}</span> : null}</p>
    <p className="mt-1 text-2xl font-bold text-notaria-yellow tabular-nums">{Number(value || 0)}</p>
  </article>
);

const EvidenceBadge = ({ evidence }) => (
  <span className="inline-flex flex-col gap-1 rounded-md bg-notaria-blue-2 px-3 py-2 text-xs ring-1 ring-white/10">
    <span className="font-bold text-notaria-yellow">{evidenceCode(evidence)}</span>
    <span className="font-semibold text-gray-100">{evidenceLabel(evidence)}</span>
  </span>
);

const DetailPanel = ({ row }) => (
  <div className="rounded-md bg-notaria-blue-2/70 p-3 text-xs text-gray-100">
    <dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      <div><dt className="text-gray-300">Base de evidencia</dt><dd className="font-semibold">{row.evidence_basis || 'NO DISPONIBLE'}</dd></div>
       <div><dt className="text-gray-300">Snapshot documental</dt><dd>{row.historical_document_snapshot ? 'Sí' : 'No'}</dd></div>
       <div><dt className="text-gray-300">Archivo</dt><dd>{proofSourceLabel(row.proof_source)}</dd></div>
       <div><dt className="text-gray-300">Trámite IDs</dt><dd>{(row.tramite_ids || []).join(', ') || 'NO DISPONIBLE'}</dd></div>
      <div><dt className="text-gray-300">Fechas documento</dt><dd>{(row.document_dates || []).join(', ') || 'NO DISPONIBLE'}</dd></div>
    </dl>
    {row.detail_warning && (
      <p className="mt-3 rounded bg-amber-950/40 p-2 text-amber-100">{row.detail_warning}</p>
    )}
  </div>
);

const ProofModal = ({ row, onClose, scrollRootRef }) => {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const scrollRoot = scrollRootRef?.current;
    const previousRootOverflow = scrollRoot?.style.overflow;
    document.body.style.overflow = 'hidden';
    if (scrollRoot) scrollRoot.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const reclaimFocus = (event) => {
      if (!dialogRef.current?.contains(event.target)) {
        closeButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', reclaimFocus, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      if (scrollRoot) scrollRoot.style.overflow = previousRootOverflow;
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', reclaimFocus, true);
      if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
    };
  }, [onClose, scrollRootRef]);

  const type = proofType(row);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3 sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="deposit-proof-title"
        className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-notaria-blue-1 shadow-2xl ring-1 ring-white/15"
      >
        <header className="flex items-start justify-between gap-4 border-b border-notaria-blue-2 p-4 sm:px-6">
          <div>
            <h2 id="deposit-proof-title" className="text-lg font-bold text-white">Comprobante de depósito</h2>
            <p className="mt-1 text-sm text-gray-300">{row.reference || `COMPROBANTE #${row.comprobante_id}`}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Cerrar comprobante"
            className="shrink-0 rounded-md p-2 text-gray-200 hover:bg-notaria-blue-2 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </header>
        <div className="grid gap-3 border-b border-notaria-blue-2 bg-notaria-blue-2/60 p-4 sm:grid-cols-2 sm:px-6">
          <div>
            <p className="text-xs font-semibold uppercase text-gray-300">Monto justificado</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-notaria-yellow">{money(row.amount)}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-xs font-semibold uppercase text-gray-300">Fuente del archivo</p>
            <p className="mt-1 text-sm font-bold text-white">{proofSourceLabel(row.proof_source)}</p>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-black/20 p-3 sm:p-6">
          {type === 'image' ? (
            <img src={row.proof_url} alt={`Comprobante ${row.reference || row.comprobante_id}`} className="mx-auto max-h-[70vh] max-w-full object-contain" />
          ) : type === 'pdf' ? (
            <iframe
              title="Vista previa del comprobante"
              src={row.proof_url}
              sandbox=""
              referrerPolicy="no-referrer"
              tabIndex={-1}
              onFocus={() => closeButtonRef.current?.focus()}
              className="h-[70vh] w-full rounded border-0 bg-white"
            />
          ) : null}
        </div>
      </section>
    </div>
  );
};

const HistoryTable = ({ rows, status, selectedProof, onSelectProof, onCloseProof, scrollRootRef }) => {
  const [expandedId, setExpandedId] = useState(null);

  if (status === 'loading') {
    return (
      <section role="status" aria-label="Cargando historial" className="rounded-lg bg-notaria-blue-1/80 p-5 text-gray-200">
        Cargando historial...
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section role="alert" className="rounded-lg bg-red-950/50 p-5 text-red-100">
        No se pudo cargar el historial de aprobaciones.
      </section>
    );
  }

  if (!rows.length) {
    return (
      <section className="rounded-lg bg-notaria-blue-1/80 p-5 text-center text-gray-300">
        No hay aprobaciones para los filtros seleccionados.
      </section>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg bg-notaria-blue-1/80 shadow-xl ring-1 ring-white/5">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-notaria-blue-2 text-left">
            <th className="p-3">Evidencia</th>
            <th className="p-3">Comprobante</th>
            <th className="p-3">Referencia</th>
            <th className="p-3">Matrizador</th>
            <th className="p-3">Monto</th>
            <th className="p-3">Aprobador</th>
            <th className="p-3">Decisión</th>
             <th className="p-3">Estado actual</th>
             <th className="p-3">Archivo</th>
             <th className="p-3">Detalle</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="border-b border-notaria-blue-2 align-top hover:bg-notaria-blue-2/40">
                <td className="p-3 font-semibold"><EvidenceBadge evidence={row.evidence} /></td>
                <td className="p-3 tabular-nums">#{row.comprobante_id}</td>
                <td className="p-3">{row.reference || 'NO DISPONIBLE'}</td>
                <td className="p-3">{joinUpper(row.matrizadores)}</td>
                <td className="p-3 font-semibold tabular-nums">{money(row.amount)}</td>
                <td className="p-3">{row.actor_username || 'NO DISPONIBLE'}</td>
                 <td className="p-3">{formatDateTime(row.decision_at)}</td>
                 <td className="p-3">{row.current_state || 'NO DISPONIBLE'}</td>
                 <td className="p-3">
                   {proofType(row) ? (
                     <button
                       type="button"
                       onClick={() => onSelectProof(row)}
                       aria-label={`Ver comprobante ${row.comprobante_id}`}
                       className="inline-flex items-center gap-1 rounded-md bg-notaria-blue-2 px-3 py-2 font-semibold hover:bg-notaria-blue-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow"
                     >
                       <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                       Ver comprobante
                     </button>
                   ) : <span className="font-semibold text-gray-300">NO DISPONIBLE</span>}
                 </td>
                 <td className="p-3">
                  <button
                    type="button"
                    onClick={() => setExpandedId((current) => (current === row.id ? null : row.id))}
                    aria-expanded={expandedId === row.id}
                    className="inline-flex items-center gap-1 rounded-md bg-notaria-blue-2 px-3 py-2 font-semibold hover:bg-notaria-blue-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow"
                    aria-label={`${expandedId === row.id ? 'Ocultar' : 'Ver'} detalle del comprobante ${row.comprobante_id}`}
                  >
                    {expandedId === row.id ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                    Detalle
                  </button>
                </td>
              </tr>
              {expandedId === row.id && (
                <tr className="border-b border-notaria-blue-2">
                  <td className="p-3" colSpan="10"><DetailPanel row={row} /></td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {selectedProof && <ProofModal row={selectedProof} onClose={onCloseProof} scrollRootRef={scrollRootRef} />}
    </div>
  );
};

const DepositApprovalHistory = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const hasPermission = Boolean(user?.role === 'notario' || user?.is_superuser);
  const requestRef = useRef(0);
  const viewRootRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [payload, setPayload] = useState({ total: 0, counts: {}, results: [] });
  const [selectedProof, setSelectedProof] = useState(null);
  const [evidence, setEvidence] = useState('verified');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    date_from: minDate,
    date_to: todayISO(),
    matrizador: '',
    approver: '',
    search: '',
    page_size: 25,
  });

  useEffect(() => {
    if (!loading && !hasPermission) {
      toast.error('No tienes permisos para acceder al historial de aprobaciones.');
      navigate('/login');
    }
  }, [hasPermission, loading, navigate]);

  const buildParams = useCallback((overrides = {}) => {
    const nextFilters = { ...filters, ...(overrides.filters || {}) };
    const nextEvidence = overrides.evidence || evidence;
    const nextPage = overrides.page || page;
    const value = {
      evidence: nextEvidence,
      date_from: nextFilters.date_from || minDate,
      date_to: nextFilters.date_to || nextFilters.date_from || minDate,
      page: nextPage,
      page_size: Number(nextFilters.page_size) || 25,
    };
    if (nextFilters.matrizador.trim()) value.matrizador = nextFilters.matrizador.trim();
    if (nextFilters.search.trim()) value.search = nextFilters.search.trim();
    if (nextEvidence === 'verified' && nextFilters.approver.trim()) value.approver = nextFilters.approver.trim();
    return value;
  }, [evidence, filters, page]);

  const params = useMemo(() => buildParams(), [buildParams]);

  const closeProof = useCallback(() => setSelectedProof(null), []);

  const fetchHistory = useCallback(async (requestParams = params) => {
    if (!hasPermission) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    closeProof();
    setStatus('loading');
    try {
      const response = await api.get(endpoint, { params: requestParams });
      if (requestRef.current !== requestId) return;
      setPayload(response.data || { total: 0, counts: {}, results: [] });
      setStatus('ready');
    } catch (error) {
      if (requestRef.current !== requestId) return;
      setPayload({ total: 0, counts: {}, results: [] });
      setStatus('error');
    }
  }, [closeProof, hasPermission, params]);

  useEffect(() => {
    if (!loading && hasPermission) {
      fetchHistory();
    }
  }, [fetchHistory, hasPermission, loading]);

  const total = Number(payload.total || 0);
  const pageSize = Number(filters.page_size) || 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rows = Array.isArray(payload.results) ? payload.results : [];
  const counts = payload.counts || {};

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const applyFilters = () => {
    const nextParams = buildParams({ page: 1 });
    setPage(1);
    fetchHistory(nextParams);
  };

  const switchEvidence = (nextEvidence) => {
    setEvidence(nextEvidence);
    setPage(1);
  };

  if (loading) return <div className="h-screen overflow-y-auto bg-gray-900 p-6 text-white">Cargando...</div>;
  if (!hasPermission) return null;

  return (
    <motion.div ref={viewRootRef} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="h-screen overflow-y-auto bg-gray-900 p-4 text-white sm:p-6">
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
      <main className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/dashboard/notario" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-300 hover:text-notaria-yellow focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow">
              <ArrowLeftIcon className="h-4 w-4" /> Volver
            </Link>
            <h1 className="mt-3 text-2xl font-bold sm:text-3xl">Historial de aprobaciones</h1>
            <p className="text-sm text-gray-300">Desde 2026-02-01. Los registros legacy sin snapshot usan vínculos actuales señalados.</p>
          </div>
          <button
            type="button"
            onClick={() => fetchHistory(params)}
            className="inline-flex items-center gap-2 rounded-lg bg-notaria-blue-2 px-4 py-2 font-semibold hover:bg-notaria-blue-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-notaria-yellow"
          >
            <ArrowPathIcon className={`h-5 w-5 ${status === 'loading' ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </header>

        <section aria-label="Conteos por evidencia" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <CountCard label="Sistema verificado" code="SYSTEM" value={counts.verified_system} />
          <CountCard label="Legacy verificado" code="LEGACY" value={counts.verified_legacy} />
          <CountCard label="Sin bitácora" code="SIN BITACORA" value={counts.state_only} />
        </section>

        <section aria-label="Leyenda de evidencia" className="grid grid-cols-1 gap-2 rounded-lg bg-notaria-blue-1/80 p-4 text-sm text-gray-100 ring-1 ring-white/5 sm:grid-cols-3">
          <p><span className="font-bold text-notaria-yellow">SYSTEM</span> Sistema verificado con snapshot documental.</p>
          <p><span className="font-bold text-notaria-yellow">LEGACY</span> Legacy verificado; puede usar vínculo actual si no hay snapshot.</p>
          <p><span className="font-bold text-notaria-yellow">SIN BITACORA</span> Estado aprobado sin evento approved; actor y fecha no disponibles.</p>
        </section>

        <section className="rounded-lg bg-notaria-blue-1/80 p-4 shadow-xl ring-1 ring-white/5">
          <div className="mb-4 grid grid-cols-2 gap-2" role="tablist" aria-label="Tipo de evidencia">
            <button
              type="button"
              role="tab"
              aria-selected={evidence === 'verified'}
              onClick={() => switchEvidence('verified')}
              className={`rounded-md px-4 py-2 font-bold ${evidence === 'verified' ? 'bg-notaria-yellow text-notaria-blue-1' : 'bg-notaria-blue-2 text-white hover:bg-notaria-blue-3'}`}
            >
              Verificables
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={evidence === 'state_only'}
              onClick={() => switchEvidence('state_only')}
              className={`rounded-md px-4 py-2 font-bold ${evidence === 'state_only' ? 'bg-notaria-yellow text-notaria-blue-1' : 'bg-notaria-blue-2 text-white hover:bg-notaria-blue-3'}`}
            >
              Sin bitácora
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-200">
              Desde
              <input type="date" min={minDate} value={filters.date_from} onChange={(event) => updateFilter('date_from', event.target.value)} className="rounded-md bg-notaria-blue-2 px-3 py-2 text-white" />
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-200">
              Hasta
              <input type="date" min={filters.date_from || minDate} value={filters.date_to} onChange={(event) => updateFilter('date_to', event.target.value)} className="rounded-md bg-notaria-blue-2 px-3 py-2 text-white" />
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-200">
              Matrizador
              <input aria-label="Matrizador" value={filters.matrizador} onChange={(event) => updateFilter('matrizador', event.target.value)} className="rounded-md bg-notaria-blue-2 px-3 py-2 text-white" />
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-200">
              Aprobador
              <input aria-label="Aprobador" value={filters.approver} disabled={evidence !== 'verified'} onChange={(event) => updateFilter('approver', event.target.value)} className="rounded-md bg-notaria-blue-2 px-3 py-2 text-white disabled:opacity-50" />
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-200">
              Buscar
              <input aria-label="Buscar" value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} className="rounded-md bg-notaria-blue-2 px-3 py-2 text-white" />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={applyFilters} className="w-full rounded-md bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white">
                Aplicar filtros
              </button>
            </div>
          </div>
        </section>

        <HistoryTable
          rows={rows}
          status={status}
          selectedProof={selectedProof}
          onSelectProof={setSelectedProof}
          onCloseProof={closeProof}
          scrollRootRef={viewRootRef}
        />

        <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-notaria-blue-1/80 p-4 text-sm">
          <p className="text-gray-300">Mostrando {rows.length} de {total} registros</p>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="history-page-size" className="text-gray-300">Filas</label>
            <select
              id="history-page-size"
              aria-label="Filas"
              value={filters.page_size}
              onChange={(event) => {
                updateFilter('page_size', Number(event.target.value));
                setPage(1);
              }}
              className="rounded-md bg-notaria-blue-2 px-3 py-2 text-white"
            >
              {pageSizeOptions.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <button
              type="button"
              aria-label="Página anterior"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="rounded-md bg-notaria-blue-2 p-2 hover:bg-notaria-blue-3 disabled:opacity-40"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span>Pagina {page} de {totalPages}</span>
            <button
              type="button"
              aria-label="Página siguiente"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              className="rounded-md bg-notaria-blue-2 p-2 hover:bg-notaria-blue-3 disabled:opacity-40"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </section>
      </main>
    </motion.div>
  );
};

export default DepositApprovalHistory;
